import type { FirebaseTransaction, Invoice, InvoicePaymentConfirmation } from '../webapp/src/types';

export const normalizePaymentIban = (value?: string) => (value ?? '').replace(/\s+/g, '').toUpperCase();

/** A bank confirmation must match the fee collection account and currency. */
export function transactionConfirmedPayment(transaction: FirebaseTransaction): number {
    const seen = new Set<string>();
    const confirmations = transaction.payment_confirmations?.length
        ? transaction.payment_confirmations : transaction.payment_confirmation ? [transaction.payment_confirmation] : [];
    return confirmations.reduce((total, confirmation) => {
        if (!confirmation.transactionID || seen.has(confirmation.transactionID) ||
            confirmation.currency !== 'KWD' ||
            !normalizePaymentIban(transaction.securityTransferFeeIban) ||
            normalizePaymentIban(confirmation.toIban) !== normalizePaymentIban(transaction.securityTransferFeeIban)) return total;
        const amount = confirmation.amountInBaseCurrency;
        if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0 || !Number.isSafeInteger(Math.round(amount * 1000))) return total;
        seen.add(confirmation.transactionID);
        return total + Math.round(amount * 1000);
    }, 0) / 1000;
}

export function transactionPaidByBank(transaction: FirebaseTransaction): boolean {
    return (transaction.securityTransferFee ?? 0) > 0 &&
        Math.round(transactionConfirmedPayment(transaction) * 1000) >= Math.round(transaction.securityTransferFee! * 1000);
}

export function transactionFeeInvoice(transaction: FirebaseTransaction, id: string, civilID: string, iban: string): Invoice | undefined {
    if (!['security_in', 'security_out'].includes(transaction.transactionType) ||
        transaction.tags?.transactionStatus?.code !== 'need_slip' || !transaction.paymentSlipRequired ||
        !Number.isFinite(transaction.securityTransferFee) || (transaction.securityTransferFee ?? 0) <= 0 || !normalizePaymentIban(iban)) return;
    const amount = (Math.round(transaction.securityTransferFee! * 1000) - Math.round(transactionConfirmedPayment({ ...transaction, securityTransferFeeIban: iban }) * 1000)) / 1000;
    if (amount <= 0) return;
    const count = transaction.securityTransferSecurityCount ?? 0;
    const portfolioNumber = String((transaction.product as { KFHTRADE_ID?: string } | undefined)?.KFHTRADE_ID ?? '').trim();
    if (!portfolioNumber) throw new Error('Shares transfer invoice requires the client KFHTRADE_ID');
    return {
        id, type: 'transaction', civilID, currency: 'KWD', iban: normalizePaymentIban(iban), amount,
        titleEn: 'Securities Transfer Fees', titleAr: 'رسوم تحويل الأوراق المالية',
        descriptionEn: transaction.transactionType === 'security_in'
            ? `Shares Deposit – ( ${portfolioNumber} ).`
            : `Shares Withdrawal - ( ${portfolioNumber} ).`,
        descriptionAr: `رسوم تحويل ${count} أوراق مالية × 6.250 د.ك`,
        date: (transaction.created as any)?.toDate?.() ?? new Date(),
    };
}

export function sameTransactionPayment(a: InvoicePaymentConfirmation, b: InvoicePaymentConfirmation): boolean {
    return a.id === b.id && a.type === b.type && a.transactionID === b.transactionID &&
        a.amountInBaseCurrency === b.amountInBaseCurrency &&
        normalizePaymentIban(a.fromIban) === normalizePaymentIban(b.fromIban) &&
        normalizePaymentIban(a.toIban) === normalizePaymentIban(b.toIban) &&
        (a.currency ?? 'KWD') === (b.currency ?? 'KWD');
}
